'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Contact } from '@/lib/services/contacts/types';
import { getContactDisplayName } from '@/lib/services/contacts/utils';
import type { VehicleTransferBeneficiaryDraft } from './constants';

export function VehicleBeneficiarySelection({
  contacts,
  transferBeneficiaries,
  onBeneficiaryToggle
}: {
  contacts: Contact[];
  transferBeneficiaries?: VehicleTransferBeneficiaryDraft[];
  onBeneficiaryToggle: (contact: Contact) => void;
}) {
  const selected = transferBeneficiaries ?? [];
  return (
    <div className="space-y-1.5">
      <label className="text-foreground-secondary text-sm font-medium">
        Transfer to
      </label>
      <div className="space-y-1">
        {contacts.map((contact) => {
          const isSelected = selected.some((b) => b.contactId === contact.id);
          const name = getContactDisplayName(contact);
          return (
            <div
              key={contact.id}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-1.5 transition-colors',
                isSelected
                  ? 'border-border-strong bg-surface-subtle/40'
                  : 'border-border-subtle bg-card'
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onBeneficiaryToggle(contact)}
                id={`veh-ben-inv-${contact.id}`}
              />
              <Label
                htmlFor={`veh-ben-inv-${contact.id}`}
                className="text-foreground flex-1 cursor-pointer text-sm"
              >
                {name}
              </Label>
            </div>
          );
        })}
      </div>
      {selected.length > 1 && (
        <p className="text-muted-foreground text-xs">
          Vehicles transfer jointly, with no percentage split.
        </p>
      )}
    </div>
  );
}

export function VehicleContraryIntentionCheckbox({
  hasContraryIntention,
  onContraryIntentionChange
}: {
  hasContraryIntention?: boolean;
  onContraryIntentionChange?: (value: boolean) => void;
}) {
  return (
    <div className="border-border bg-surface-subtle/30 flex items-start gap-2 rounded-lg border p-2.5">
      <Checkbox
        id="veh-contrary-intention-inv"
        checked={!!hasContraryIntention}
        onCheckedChange={(val) => onContraryIntentionChange?.(val === true)}
      />
      <div>
        <Label
          htmlFor="veh-contrary-intention-inv"
          className="text-foreground cursor-pointer text-sm font-medium"
        >
          Contrary intention: estate pays off the loan
        </Label>
        <p className="text-muted-foreground mt-0.5 text-xs">
          The will directs the estate to pay off the vehicle loan before
          transferring.
        </p>
      </div>
    </div>
  );
}
